import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { postVoiceSession } from '../api/voice-bot-client'
import type { Message, ScheduledMeeting, VoiceSessionData } from '../types'

export interface StartVoiceSessionDeps<C> {
  config: C
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sessionDataRef: MutableRefObject<any>
  currentBotResponseRef: MutableRefObject<string>
  goodbyeSentRef: MutableRefObject<boolean>
  initialGreetingCompleteRef: MutableRefObject<boolean>
  preGreetingAudioCommitsRef: MutableRefObject<number>
  transcriptionsToSkipRef: MutableRefObject<number>
  pendingGreetingUnmuteRef: MutableRefObject<boolean>
  setError: Dispatch<SetStateAction<string | null>>
  setIsConnecting: Dispatch<SetStateAction<boolean>>
  setTranscript: Dispatch<SetStateAction<Message[]>>
  setScheduledMeeting: Dispatch<SetStateAction<ScheduledMeeting | null>>
  setShowCalendlyEmbed: Dispatch<SetStateAction<boolean>>
  setBookingConfirmed: Dispatch<SetStateAction<boolean>>
  setPendingCallEnd: Dispatch<SetStateAction<boolean>>
  setPendingEndAfterSpeaking: Dispatch<SetStateAction<boolean>>
  setIsMuted: Dispatch<SetStateAction<boolean>>
  setConfig: Dispatch<SetStateAction<C>>
  setPropertyName: Dispatch<SetStateAction<string | null>>
  initializeWebRTC: (
    ephemeralKey: string,
    propertyName: string | null,
    activeConfig: C,
  ) => Promise<void>
}

export const startVoiceSession = async <C extends {
  voice: string
  temperature: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}>(
  deps: StartVoiceSessionDeps<C>,
): Promise<void> => {
  const {
    config,
    sessionDataRef,
    currentBotResponseRef,
    goodbyeSentRef,
    initialGreetingCompleteRef,
    preGreetingAudioCommitsRef,
    transcriptionsToSkipRef,
    pendingGreetingUnmuteRef,
    setError,
    setIsConnecting,
    setTranscript,
    setScheduledMeeting,
    setShowCalendlyEmbed,
    setBookingConfirmed,
    setPendingCallEnd,
    setPendingEndAfterSpeaking,
    setIsMuted,
    setConfig,
    setPropertyName,
    initializeWebRTC,
  } = deps

  try {
    setError(null)
    setIsConnecting(true)
    setTranscript([])
    currentBotResponseRef.current = ''
    setScheduledMeeting(null)
    setShowCalendlyEmbed(false)
    setBookingConfirmed(false)
    setPendingCallEnd(false)
    setPendingEndAfterSpeaking(false)
    goodbyeSentRef.current = false

    // Reset initial greeting flag - user will be muted until greeting completes
    initialGreetingCompleteRef.current = false
    preGreetingAudioCommitsRef.current = 0
    transcriptionsToSkipRef.current = 0
    pendingGreetingUnmuteRef.current = false

    // Start with UI mute ON - prevents user from speaking during greeting
    setIsMuted(true)

    const requestBody = {
      voice: config.voice,
      temperature: config.temperature,
      propertyCode: config.propertyCode,
      forceVoiceOverride: config.forceVoiceOverride === true,
    }
    console.log('🔍 API Request Body:', requestBody)

    const response = await postVoiceSession(requestBody, config.publicKey)

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Unknown error' }))

      if (response.status === 401) {
        if (errorData.error === 'Missing authentication') {
          throw new Error('שירות זה דורש הזדהות. אנא צור קשר עם מנהל המערכת.\n\nThis service requires authentication. Please contact your administrator.')
        }
        if (errorData.error === 'Invalid public key') {
          throw new Error('מפתח הזדהות לא תקין. אנא צור קשר עם מנהל המערכת.\n\nInvalid authentication key. Please contact your administrator.')
        }
      }

      if (response.status === 403) {
        if (errorData.error === 'Account inactive') {
          throw new Error('החשבון אינו פעיל. אנא צור קשר עם התמיכה.\n\nAccount is inactive. Please contact support.')
        }
        if (errorData.error === 'Trial expired') {
          throw new Error('תקופת הניסיון הסתיימה. אנא צור קשר עם התמיכה.\n\nTrial period has ended. Please contact support.')
        }
        if (errorData.error === 'Usage limit exceeded') {
          throw new Error('חרגת ממכסת השימוש החודשית. אנא צור קשר עם התמיכה.\n\nMonthly usage limit exceeded. Please contact support.')
        }
        if (errorData.error === 'Minutes depleted') {
          throw new Error('נגמרו לך דקות השיחה. אנא צור קשר עם התמיכה לטעינה מחדש.\n\nYour call minutes have been depleted. Please contact support to top up.')
        }
      }

      throw new Error(errorData.message || errorData.error || 'Failed to create voice session')
    }

    const data: VoiceSessionData = await response.json()
    console.log('🔍 API Response Data:', data)
    sessionDataRef.current = data

    if (data.customerId) {
      sessionDataRef.current.customerId = data.customerId
    }

    // Apply widgetConfig from server (overrides local config with DB values)
    let activeConfig: C = config
    if (data.widgetConfig) {
      console.log('📋 Applying server widgetConfig:', data.widgetConfig)
      activeConfig = { ...config, ...data.widgetConfig }
      setConfig(activeConfig)
    } else if (data.voiceHardening) {
      activeConfig = { ...config, voiceHardening: data.voiceHardening }
      setConfig(activeConfig)
    }

    if (data.propertyName) {
      setPropertyName(data.propertyName)
      console.log(`🏡 Property context received: ${data.propertyName}`)
    } else {
      console.log('⚠️  No property context in response (propertyName is null/undefined)')
    }

    await initializeWebRTC(data.ephemeralKey, data.propertyName || null, activeConfig)
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to start session'
    console.error('❌ Start session error:', err)
    setError(errorMessage)
    setIsConnecting(false)
  }
}
